/**
 * UV editing of `mesh` parts (schema.md "UV editing"): the layout of a part's
 * UVs, its islands and distortion, selection in the four UV select modes, and
 * the simple transforms the UV Editor applies straight to the document
 * (move / rotate / scale / flip / align / pin / seams / texel density).
 * Heavier operators (unwrap, projections, pack, stitch, ...) run in the engine
 * as `modelOp`s (runtime/uv_tool.rs).
 *
 * Format (`MeshShape`):
 *   uvs      [[u, v], ...]           texture coordinates
 *   uvFaces  [[uvIndex, ...], ...]   parallel to `faces`; corner k of face f uses uvs[uvFaces[f][k]].
 *                                    Empty + `uvs.length === vertices.length` = legacy per-vertex UVs.
 *                                    Shared uv indices are connected (one island); seams split them.
 *   uvPins   [uvIndex, ...]          pinned UVs (unwrap keeps them in place)
 *   seams    [[a, b], ...]           vertex pairs where unwrapping cuts
 *
 * Everything here is pure: edits return new arrays / documents.
 */

import { partAt, updatePart, type IndexPath, type ModelDoc, type ModelShape } from './model-doc.js';

export type Vec2 = [number, number];
export type UvSelectMode = 'vertex' | 'edge' | 'face' | 'island';

/** A `mesh` shape with the fields the UV editor reads and writes. */
export interface MeshShape extends ModelShape {
  type: 'mesh';
  vertices: number[][];
  faces: number[][];
  uvs?: number[][];
  uvFaces?: number[][];
  uvPins?: number[];
  seams?: number[][];
  creases?: number[][];
  sharp?: number[][];
}

/**
 * A part's UVs ready to display: for `mesh` parts built here from the document
 * ({@link layoutFromMesh}), for other parts sent by the engine (`uvLayout`
 * event, read-only).
 */
export interface UvLayout {
  /** Prism part path. */
  path: string;
  editable: boolean;
  uvs: Vec2[];
  /** UV index per face corner. */
  faces: number[][];
  /** Vertex index per face corner (same shape as `faces`). */
  meshFaces: number[][];
  /** Face indices per island. */
  islands: number[][];
  /** Angle distortion per face, 0 (none) .. 1. */
  stretch: number[];
  /** Area distortion per face, 0 (none) .. 1. */
  areaStretch: number[];
  /** Faces overlapping another face in UV space. */
  overlap: number[];
  /** Mean UV units per meter. */
  texelDensity: number;
  faceArea3d: number[];
  faceAreaUv: number[];
  /** Seams as vertex pairs. */
  seams: [number, number][];
  pins: number[];
}

/* ------------------------------------------------------------------ */
/* Reading the document                                               */

function isNumberArray(value: unknown, length?: number): value is number[] {
  return Array.isArray(value) && (length === undefined || value.length === length) && value.every((n) => typeof n === 'number' && Number.isFinite(n));
}

function numberLists(value: unknown): number[][] {
  return Array.isArray(value) ? value.filter((f): f is number[] => isNumberArray(f)) : [];
}

export function isMeshShape(shape: ModelShape | undefined | null): shape is MeshShape {
  return Boolean(shape && shape.type === 'mesh');
}

/** The `mesh` shape of the part at `path`, or null. */
export function meshShapeAt(doc: ModelDoc, path: IndexPath): MeshShape | null {
  const shape = partAt(doc, path)?.shape;
  return isMeshShape(shape) ? shape : null;
}

/**
 * UV index per face corner: `uvFaces` when it matches `faces`, the vertex
 * indices for legacy per-vertex UVs, or null when the mesh has no UVs.
 */
export function resolveUvFaces(shape: MeshShape): number[][] | null {
  const faces = numberLists(shape.faces);
  const uvs = Array.isArray(shape.uvs) ? shape.uvs : [];
  const uvFaces = numberLists(shape.uvFaces);
  if (uvs.length === 0) return null;
  if (uvFaces.length > 0) {
    if (uvFaces.length !== faces.length) return null;
    for (let f = 0; f < faces.length; f++) {
      const uf = uvFaces[f]!;
      if (uf.length !== faces[f]!.length || uf.some((i) => i < 0 || i >= uvs.length)) return null;
    }
    return uvFaces;
  }
  const vertices = Array.isArray(shape.vertices) ? shape.vertices : [];
  if (uvs.length !== vertices.length) return null;
  return faces.map((f) => f.slice());
}

function toVec2(value: unknown): Vec2 {
  return Array.isArray(value) && typeof value[0] === 'number' && typeof value[1] === 'number' ? [value[0], value[1]] : [0, 0];
}

function toPairs(value: unknown): [number, number][] {
  return numberLists(value)
    .filter((p) => p.length >= 2)
    .map((p) => [p[0]!, p[1]!]);
}

/** Layout of a `mesh` part's UVs with islands, distortion and (optionally) overlaps; null without UVs. */
export function layoutFromMesh(shape: MeshShape, path: string, options: { overlap?: boolean } = {}): UvLayout | null {
  const faces = resolveUvFaces(shape);
  if (!faces) return null;
  const uvs = (shape.uvs ?? []).map(toVec2);
  const meshFaces = numberLists(shape.faces);
  const positions = (shape.vertices ?? []).map((v) => [Number(v[0]) || 0, Number(v[1]) || 0, Number(v[2]) || 0] as [number, number, number]);
  const metrics = stretchMetrics(uvs, faces, positions, meshFaces);
  return {
    path,
    editable: true,
    uvs,
    faces,
    meshFaces,
    islands: faceIslands(faces, uvs.length).islands,
    stretch: metrics.angle,
    areaStretch: metrics.area,
    overlap: options.overlap ? overlappingFaces(uvs, faces) : [],
    texelDensity: metrics.density,
    faceArea3d: metrics.area3d,
    faceAreaUv: metrics.areaUv,
    seams: toPairs(shape.seams),
    pins: isNumberArray(shape.uvPins) ? shape.uvPins.filter((i) => i >= 0 && i < uvs.length) : [],
  };
}

/** The engine's `uvLayout` event payload (defensive: missing fields become empty). */
export function parseUvLayout(raw: Record<string, unknown>): UvLayout | null {
  if (typeof raw.path !== 'string') return null;
  const uvs = Array.isArray(raw.uvs) ? raw.uvs.map(toVec2) : [];
  const faces = numberLists(raw.faces).filter((f) => f.every((i) => i >= 0 && i < uvs.length));
  const meshFaces = numberLists(raw.meshFaces);
  const nums = (v: unknown) => (isNumberArray(v) ? v : []);
  const area3d = nums(raw.faceArea3d);
  const areaUv = nums(raw.faceAreaUv);
  const islands = numberLists(raw.islands);
  return {
    path: raw.path,
    editable: raw.editable === true,
    uvs,
    faces,
    meshFaces: meshFaces.length === faces.length ? meshFaces : faces,
    islands: islands.length ? islands : faceIslands(faces, uvs.length).islands,
    stretch: nums(raw.stretch),
    areaStretch: area3d.length === faces.length && areaUv.length === faces.length ? areaStretchOf(area3d, areaUv) : [],
    overlap: nums(raw.overlap),
    texelDensity: typeof raw.texelDensity === 'number' && Number.isFinite(raw.texelDensity) ? raw.texelDensity : 0,
    faceArea3d: area3d,
    faceAreaUv: areaUv,
    seams: toPairs(raw.seams),
    pins: nums(raw.pins),
  };
}

/* ------------------------------------------------------------------ */
/* Topology                                                           */

/** Faces grouped into islands: faces sharing a uv index are connected. */
export function faceIslands(faces: readonly (readonly number[])[], uvCount: number): { islands: number[][]; faceIsland: number[] } {
  const parent = faces.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]!]!;
      i = parent[i]!;
    }
    return i;
  };
  const owner = new Int32Array(Math.max(0, uvCount)).fill(-1);
  faces.forEach((face, f) => {
    for (const uv of face) {
      if (uv < 0 || uv >= uvCount) continue;
      const o = owner[uv]!;
      if (o < 0) owner[uv] = f;
      else {
        const a = find(o);
        const b = find(f);
        if (a !== b) parent[Math.max(a, b)] = Math.min(a, b);
      }
    }
  });
  const index = new Map<number, number>();
  const islands: number[][] = [];
  const faceIsland = faces.map((_, f) => {
    const root = find(f);
    let i = index.get(root);
    if (i === undefined) {
      i = islands.length;
      index.set(root, i);
      islands.push([]);
    }
    islands[i]!.push(f);
    return i;
  });
  return { islands, faceIsland };
}

export interface UvEdge {
  a: number;
  b: number;
  /** Faces using this uv edge (1 = island boundary). */
  faces: number[];
}

const edgeKey = (a: number, b: number) => (a < b ? `${a},${b}` : `${b},${a}`);

/** Unique UV edges (`a < b`) with the faces that use them. */
export function uvEdges(faces: readonly (readonly number[])[]): UvEdge[] {
  const map = new Map<string, UvEdge>();
  faces.forEach((face, f) => {
    for (let k = 0; k < face.length; k++) {
      const a = face[k]!;
      const b = face[(k + 1) % face.length]!;
      if (a === b) continue;
      const key = edgeKey(a, b);
      const edge = map.get(key);
      if (edge) {
        if (!edge.faces.includes(f)) edge.faces.push(f);
      } else map.set(key, { a: Math.min(a, b), b: Math.max(a, b), faces: [f] });
    }
  });
  return [...map.values()];
}

/** Vertex edge (sorted pair key) of each uv edge, through the face corners. */
function uvToVertexEdges(layout: Pick<UvLayout, 'faces' | 'meshFaces'>): Map<string, [number, number]> {
  const out = new Map<string, [number, number]>();
  layout.faces.forEach((face, f) => {
    const mesh = layout.meshFaces[f];
    if (!mesh || mesh.length !== face.length) return;
    for (let k = 0; k < face.length; k++) {
      const k2 = (k + 1) % face.length;
      const va = mesh[k]!;
      const vb = mesh[k2]!;
      out.set(edgeKey(face[k]!, face[k2]!), va < vb ? [va, vb] : [vb, va]);
    }
  });
  return out;
}

/** UV edges lying on a seam (vertex pair listed in `layout.seams`). */
export function seamUvEdges(layout: Pick<UvLayout, 'faces' | 'meshFaces' | 'seams'>): [number, number][] {
  if (!layout.seams.length) return [];
  const seams = new Set(layout.seams.map(([a, b]) => edgeKey(a, b)));
  const out: [number, number][] = [];
  for (const [key, [va, vb]] of uvToVertexEdges(layout)) {
    if (!seams.has(edgeKey(va, vb))) continue;
    const [a, b] = key.split(',').map(Number) as [number, number];
    out.push([a, b]);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Distortion, density, overlap                                       */

type Vec3 = [number, number, number];

function len3(a: Vec3): number {
  return Math.hypot(a[0], a[1], a[2]);
}

function cross3(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

/** Area of a 3D polygon (Newell's method; any planar-ish n-gon). */
export function polygonArea3(points: readonly Vec3[]): number {
  const n: Vec3 = [0, 0, 0];
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    const c = cross3(a, b);
    n[0] += c[0];
    n[1] += c[1];
    n[2] += c[2];
  }
  return len3(n) / 2;
}

/** Signed area of a 2D polygon (counter-clockwise positive). */
export function polygonArea2(points: readonly Vec2[]): number {
  let s = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    s += a[0] * b[1] - b[0] * a[1];
  }
  return s / 2;
}

function angleBetween(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
  const la = Math.hypot(ax, ay, az);
  const lb = Math.hypot(bx, by, bz);
  if (la < 1e-12 || lb < 1e-12) return 0;
  const c = (ax * bx + ay * by + az * bz) / (la * lb);
  return Math.acos(Math.min(1, Math.max(-1, c)));
}

/** Area distortion per face: 1 - min(r, 1/r), r = the face's share of UV area over its share of 3D area. */
export function areaStretchOf(area3d: readonly number[], areaUv: readonly number[]): number[] {
  const total3d = area3d.reduce((s, a) => s + a, 0);
  const totalUv = areaUv.reduce((s, a) => s + a, 0);
  return area3d.map((a3, f) => {
    const au = areaUv[f] ?? 0;
    if (total3d <= 1e-12 || totalUv <= 1e-12) return 0;
    const s3 = a3 / total3d;
    const su = au / totalUv;
    if (s3 <= 1e-12) return su <= 1e-12 ? 0 : 1;
    if (su <= 1e-12) return 1;
    const r = su / s3;
    return 1 - Math.min(r, 1 / r);
  });
}

export interface StretchMetrics {
  /** Angle distortion per face (mean |1 - uvAngle / 3dAngle| over its corners, clamped to 0..1). */
  angle: number[];
  /** Area distortion per face (see {@link areaStretchOf}). */
  area: number[];
  area3d: number[];
  areaUv: number[];
  /** Mean UV units per meter: sqrt(total UV area / total 3D area). */
  density: number;
}

/**
 * Blender-style UV stretch: angle distortion compares each corner's angle in
 * UV space with the 3D one, area distortion compares each face's share of the
 * UV area with its share of the surface. Degenerate faces count as undistorted.
 */
export function stretchMetrics(
  uvs: readonly Vec2[],
  uvFaces: readonly (readonly number[])[],
  positions: readonly Vec3[],
  meshFaces: readonly (readonly number[])[],
): StretchMetrics {
  const angle: number[] = [];
  const area3d: number[] = [];
  const areaUv: number[] = [];
  uvFaces.forEach((uf, f) => {
    const mf = meshFaces[f] ?? [];
    const pts3 = mf.map((v) => positions[v] ?? ([0, 0, 0] as Vec3));
    const pts2 = uf.map((i) => uvs[i] ?? ([0, 0] as Vec2));
    area3d.push(pts3.length === uf.length ? polygonArea3(pts3) : 0);
    areaUv.push(Math.abs(polygonArea2(pts2)));
    if (pts3.length !== uf.length || uf.length < 3) {
      angle.push(0);
      return;
    }
    let sum = 0;
    let count = 0;
    const n = uf.length;
    for (let k = 0; k < n; k++) {
      const p = pts3[k]!;
      const prev = pts3[(k + n - 1) % n]!;
      const next = pts3[(k + 1) % n]!;
      const a3 = angleBetween(prev[0] - p[0], prev[1] - p[1], prev[2] - p[2], next[0] - p[0], next[1] - p[1], next[2] - p[2]);
      const q = pts2[k]!;
      const qp = pts2[(k + n - 1) % n]!;
      const qn = pts2[(k + 1) % n]!;
      const a2 = angleBetween(qp[0] - q[0], qp[1] - q[1], 0, qn[0] - q[0], qn[1] - q[1], 0);
      if (a3 < 1e-6) continue;
      sum += Math.abs(1 - a2 / a3);
      count++;
    }
    angle.push(count ? Math.min(1, sum / count) : 0);
  });
  const total3d = area3d.reduce((s, a) => s + a, 0);
  const totalUv = areaUv.reduce((s, a) => s + a, 0);
  return {
    angle,
    area: areaStretchOf(area3d, areaUv),
    area3d,
    areaUv,
    density: total3d > 1e-12 ? Math.sqrt(totalUv / total3d) : 0,
  };
}

/** UV units per meter over some faces (0 when they have no surface). */
export function densityOf(layout: Pick<UvLayout, 'faceArea3d' | 'faceAreaUv'>, faces: Iterable<number>): number {
  let a3 = 0;
  let au = 0;
  for (const f of faces) {
    a3 += layout.faceArea3d[f] ?? 0;
    au += layout.faceAreaUv[f] ?? 0;
  }
  return a3 > 1e-12 ? Math.sqrt(au / a3) : 0;
}

/** Texture pixels per meter for a density in UV units per meter. */
export function texelsPerMeter(density: number, textureSize: number): number {
  return density * textureSize;
}

type Tri = [Vec2, Vec2, Vec2];

function triangulate(uvs: readonly Vec2[], face: readonly number[]): Tri[] {
  const out: Tri[] = [];
  for (let k = 1; k + 1 < face.length; k++) out.push([uvs[face[0]!]!, uvs[face[k]!]!, uvs[face[k + 1]!]!]);
  return out;
}

/** Triangles overlap with positive area (separating axis test, touching does not count). */
export function trianglesOverlap(a: Tri, b: Tri, eps = 1e-7): boolean {
  if (Math.abs(polygonArea2(a)) < 1e-14 || Math.abs(polygonArea2(b)) < 1e-14) return false;
  for (const tri of [a, b]) {
    for (let i = 0; i < 3; i++) {
      const p = tri[i]!;
      const q = tri[(i + 1) % 3]!;
      const nx = q[1] - p[1];
      const ny = p[0] - q[0];
      const len = Math.hypot(nx, ny);
      if (len < 1e-14) continue;
      let minA = Infinity;
      let maxA = -Infinity;
      let minB = Infinity;
      let maxB = -Infinity;
      for (const v of a) {
        const d = (v[0] * nx + v[1] * ny) / len;
        minA = Math.min(minA, d);
        maxA = Math.max(maxA, d);
      }
      for (const v of b) {
        const d = (v[0] * nx + v[1] * ny) / len;
        minB = Math.min(minB, d);
        maxB = Math.max(maxB, d);
      }
      if (maxA <= minB + eps || maxB <= minA + eps) return false;
    }
  }
  return true;
}

/** Faces whose UVs overlap another face's (a uniform grid keeps it near-linear). */
export function overlappingFaces(uvs: readonly Vec2[], faces: readonly (readonly number[])[]): number[] {
  const tris: { face: number; tri: Tri; box: [number, number, number, number] }[] = [];
  faces.forEach((face, f) => {
    if (face.some((i) => !uvs[i])) return;
    for (const tri of triangulate(uvs, face)) {
      const xs = tri.map((p) => p[0]);
      const ys = tri.map((p) => p[1]);
      tris.push({ face: f, tri, box: [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)] });
    }
  });
  if (tris.length < 2) return [];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const t of tris) {
    minX = Math.min(minX, t.box[0]);
    minY = Math.min(minY, t.box[1]);
    maxX = Math.max(maxX, t.box[2]);
    maxY = Math.max(maxY, t.box[3]);
  }
  const cells = Math.max(1, Math.min(256, Math.ceil(Math.sqrt(tris.length))));
  const w = Math.max(maxX - minX, 1e-9) / cells;
  const h = Math.max(maxY - minY, 1e-9) / cells;
  const grid = new Map<number, number[]>();
  const cell = (x: number, lo: number, size: number) => Math.min(cells - 1, Math.max(0, Math.floor((x - lo) / size)));
  tris.forEach((t, i) => {
    for (let cx = cell(t.box[0], minX, w); cx <= cell(t.box[2], minX, w); cx++) {
      for (let cy = cell(t.box[1], minY, h); cy <= cell(t.box[3], minY, h); cy++) {
        const key = cy * cells + cx;
        const list = grid.get(key);
        if (list) list.push(i);
        else grid.set(key, [i]);
      }
    }
  });
  const hit = new Set<number>();
  const tested = new Set<number>();
  for (const list of grid.values()) {
    for (let x = 0; x < list.length; x++) {
      for (let y = x + 1; y < list.length; y++) {
        const i = list[x]!;
        const j = list[y]!;
        const a = tris[i]!;
        const b = tris[j]!;
        if (a.face === b.face || (hit.has(a.face) && hit.has(b.face))) continue;
        const pair = i < j ? i * tris.length + j : j * tris.length + i;
        if (tested.has(pair)) continue;
        tested.add(pair);
        if (a.box[2] <= b.box[0] || b.box[2] <= a.box[0] || a.box[3] <= b.box[1] || b.box[3] <= a.box[1]) continue;
        if (trianglesOverlap(a.tri, b.tri)) {
          hit.add(a.face);
          hit.add(b.face);
        }
      }
    }
  }
  return [...hit].sort((a, b) => a - b);
}

/* ------------------------------------------------------------------ */
/* Selection                                                          */

/**
 * UV selection. In vertex and edge mode `uvs` is authoritative (a face counts
 * as selected when all its corners are); in face and island mode `faces` is,
 * and `uvs` holds their corners.
 */
export interface UvSelection {
  uvs: ReadonlySet<number>;
  faces: ReadonlySet<number>;
}

export const EMPTY_SELECTION: UvSelection = { uvs: new Set(), faces: new Set() };

type Topo = Pick<UvLayout, 'faces' | 'uvs'>;

export function uvsOfFaces(layout: Topo, faces: Iterable<number>): Set<number> {
  const out = new Set<number>();
  for (const f of faces) for (const uv of layout.faces[f] ?? []) out.add(uv);
  return out;
}

/** Faces whose corners are all in `uvs`. */
export function facesOfUvs(layout: Topo, uvs: ReadonlySet<number>): Set<number> {
  const out = new Set<number>();
  layout.faces.forEach((face, f) => {
    if (face.length && face.every((uv) => uvs.has(uv))) out.add(f);
  });
  return out;
}

const facewise = (mode: UvSelectMode) => mode === 'face' || mode === 'island';

/** Build a selection from UVs or faces, completing the other half for `mode`. */
export function makeSelection(layout: Topo, mode: UvSelectMode, items: { uvs?: Iterable<number>; faces?: Iterable<number> }): UvSelection {
  if (facewise(mode)) {
    const faces = new Set(items.faces ?? facesOfUvs(layout, new Set(items.uvs ?? [])));
    if (mode === 'island') {
      const { islands, faceIsland } = faceIslands(layout.faces, layout.uvs.length);
      for (const f of [...faces]) for (const g of islands[faceIsland[f]!] ?? []) faces.add(g);
    }
    return { faces, uvs: uvsOfFaces(layout, faces) };
  }
  const uvs = new Set(items.uvs ?? uvsOfFaces(layout, items.faces ?? []));
  return { uvs, faces: facesOfUvs(layout, uvs) };
}

/** Changes when the UV topology does (not when UVs only move): selections stay valid while it holds. */
export function layoutSignature(layout: Topo): string {
  let hash = 0;
  for (const face of layout.faces) {
    for (const uv of face) hash = (Math.imul(hash, 31) + uv + 1) | 0;
    hash = (Math.imul(hash, 31) + 7919) | 0;
  }
  return `${layout.uvs.length}:${layout.faces.length}:${hash}`;
}

/** Keep what is still valid after the layout changed (faces in face modes, UVs otherwise). */
export function revalidateSelection(layout: Topo, mode: UvSelectMode, sel: UvSelection): UvSelection {
  if (facewise(mode)) return makeSelection(layout, mode, { faces: [...sel.faces].filter((f) => f < layout.faces.length) });
  return makeSelection(layout, mode, { uvs: [...sel.uvs].filter((uv) => uv < layout.uvs.length) });
}

/** Switching select mode keeps what was selected (Blender's flush rules). */
export function convertSelection(layout: Topo, sel: UvSelection, to: UvSelectMode): UvSelection {
  if (facewise(to)) return makeSelection(layout, to, { faces: sel.faces });
  return makeSelection(layout, to, { uvs: sel.uvs });
}

/** UVs used by at least one face. */
export function usedUvs(layout: Topo): Set<number> {
  const out = new Set<number>();
  for (const face of layout.faces) for (const uv of face) out.add(uv);
  return out;
}

export function selectAll(layout: Topo, mode: UvSelectMode): UvSelection {
  return facewise(mode) ? makeSelection(layout, mode, { faces: layout.faces.map((_, f) => f) }) : makeSelection(layout, mode, { uvs: usedUvs(layout) });
}

export function invertSelection(layout: Topo, mode: UvSelectMode, sel: UvSelection): UvSelection {
  if (facewise(mode)) return makeSelection(layout, mode, { faces: layout.faces.map((_, f) => f).filter((f) => !sel.faces.has(f)) });
  return makeSelection(layout, mode, { uvs: [...usedUvs(layout)].filter((uv) => !sel.uvs.has(uv)) });
}

/** Grow the selection to whole islands (Ctrl+L). */
export function selectLinked(layout: Topo, mode: UvSelectMode, sel: UvSelection): UvSelection {
  const { islands, faceIsland } = faceIslands(layout.faces, layout.uvs.length);
  const picked = new Set<number>();
  layout.faces.forEach((face, f) => {
    if (sel.faces.has(f) || face.some((uv) => sel.uvs.has(uv))) picked.add(faceIsland[f]!);
  });
  const faces = [...picked].flatMap((i) => islands[i] ?? []);
  return facewise(mode) ? makeSelection(layout, mode, { faces }) : makeSelection(layout, mode, { uvs: uvsOfFaces(layout, faces) });
}

/** The UV indices a transform moves. */
export function selectedUvIndices(sel: UvSelection): number[] {
  return [...sel.uvs].sort((a, b) => a - b);
}

/** Selected uv edges: both ends selected (vertex/edge mode) or edges of selected faces. */
export function selectedEdges(layout: Topo, mode: UvSelectMode, sel: UvSelection): [number, number][] {
  const out: [number, number][] = [];
  for (const edge of uvEdges(layout.faces)) {
    const on = facewise(mode) ? edge.faces.some((f) => sel.faces.has(f)) : sel.uvs.has(edge.a) && sel.uvs.has(edge.b);
    if (on) out.push([edge.a, edge.b]);
  }
  return out;
}

export type UvHit = { kind: 'uv'; index: number } | { kind: 'edge'; a: number; b: number } | { kind: 'face'; index: number };

function distToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? Math.min(1, Math.max(0, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

export function pointInPolygon(p: Vec2, poly: readonly Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a[1] > p[1] !== b[1] > p[1] && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1] || 1e-30) + a[0]) inside = !inside;
  }
  return inside;
}

export function faceCenter(layout: Topo, f: number): Vec2 {
  const face = layout.faces[f] ?? [];
  let u = 0;
  let v = 0;
  for (const i of face) {
    u += layout.uvs[i]?.[0] ?? 0;
    v += layout.uvs[i]?.[1] ?? 0;
  }
  return face.length ? [u / face.length, v / face.length] : [0, 0];
}

/**
 * Element under a UV-space point for the select mode: nearest UV within
 * `radius`, nearest edge within `radius`, or the face containing the point
 * (falling back to the nearest face center within `radius`).
 */
export function pick(layout: Topo, p: Vec2, mode: UvSelectMode, radius: number): UvHit | null {
  if (mode === 'vertex') {
    let best = -1;
    let bestD = radius;
    for (const uv of usedUvs(layout)) {
      const q = layout.uvs[uv];
      if (!q) continue;
      const d = Math.hypot(q[0] - p[0], q[1] - p[1]);
      if (d <= bestD) {
        bestD = d;
        best = uv;
      }
    }
    return best >= 0 ? { kind: 'uv', index: best } : null;
  }
  if (mode === 'edge') {
    let best: UvEdge | null = null;
    let bestD = radius;
    for (const edge of uvEdges(layout.faces)) {
      const d = distToSegment(p, layout.uvs[edge.a]!, layout.uvs[edge.b]!);
      if (d <= bestD) {
        bestD = d;
        best = edge;
      }
    }
    return best ? { kind: 'edge', a: best.a, b: best.b } : null;
  }
  // Last drawn (topmost) face wins.
  for (let f = layout.faces.length - 1; f >= 0; f--) {
    const poly = layout.faces[f]!.map((i) => layout.uvs[i]!);
    if (poly.length >= 3 && pointInPolygon(p, poly)) return { kind: 'face', index: f };
  }
  let best = -1;
  let bestD = radius;
  layout.faces.forEach((_, f) => {
    const c = faceCenter(layout, f);
    const d = Math.hypot(c[0] - p[0], c[1] - p[1]);
    if (d <= bestD) {
      bestD = d;
      best = f;
    }
  });
  return best >= 0 ? { kind: 'face', index: best } : null;
}

function hitItems(layout: Topo, mode: UvSelectMode, hit: UvHit): { uvs?: number[]; faces?: number[] } {
  if (hit.kind === 'uv') return { uvs: [hit.index] };
  if (hit.kind === 'edge') return { uvs: [hit.a, hit.b] };
  if (mode === 'island') {
    const { islands, faceIsland } = faceIslands(layout.faces, layout.uvs.length);
    return { faces: islands[faceIsland[hit.index]!] ?? [hit.index] };
  }
  return { faces: [hit.index] };
}

function isHitSelected(layout: Topo, mode: UvSelectMode, sel: UvSelection, hit: UvHit): boolean {
  if (hit.kind === 'uv') return sel.uvs.has(hit.index);
  if (hit.kind === 'edge') return sel.uvs.has(hit.a) && sel.uvs.has(hit.b);
  return facewise(mode) ? sel.faces.has(hit.index) : (layout.faces[hit.index] ?? []).every((uv) => sel.uvs.has(uv));
}

/** Click: replace the selection, or with `extend` (Shift) toggle the clicked element. A miss clears unless extending. */
export function clickSelect(layout: Topo, mode: UvSelectMode, sel: UvSelection, hit: UvHit | null, extend: boolean): UvSelection {
  if (!hit) return extend ? sel : makeSelection(layout, mode, {});
  const items = hitItems(layout, mode, hit);
  if (!extend) return makeSelection(layout, mode, items);
  const remove = isHitSelected(layout, mode, sel, hit);
  if (facewise(mode)) {
    const faces = new Set(sel.faces);
    for (const f of items.faces ?? []) {
      if (remove) faces.delete(f);
      else faces.add(f);
    }
    return makeSelection(layout, mode, { faces });
  }
  const uvs = new Set(sel.uvs);
  for (const uv of items.uvs ?? []) {
    if (remove) uvs.delete(uv);
    else uvs.add(uv);
  }
  return makeSelection(layout, mode, { uvs });
}

export interface UvRect {
  min: Vec2;
  max: Vec2;
}

const inRect = (p: Vec2 | undefined, r: UvRect) => Boolean(p) && p![0] >= r.min[0] && p![0] <= r.max[0] && p![1] >= r.min[1] && p![1] <= r.max[1];

/** Box select: UVs inside (vertex/edge), faces whose center is inside (face/island). `op` adds, subtracts or replaces. */
export function boxSelect(layout: Topo, mode: UvSelectMode, sel: UvSelection, rect: UvRect, op: 'set' | 'add' | 'sub'): UvSelection {
  if (facewise(mode)) {
    const inside = layout.faces.map((_, f) => f).filter((f) => inRect(faceCenter(layout, f), rect));
    let picked = new Set(inside);
    if (mode === 'island') picked = new Set(makeSelection(layout, 'island', { faces: inside }).faces);
    const faces = op === 'set' ? picked : new Set(sel.faces);
    if (op === 'add') for (const f of picked) faces.add(f);
    if (op === 'sub') for (const f of picked) faces.delete(f);
    return makeSelection(layout, mode, { faces });
  }
  const picked = [...usedUvs(layout)].filter((uv) => inRect(layout.uvs[uv], rect));
  const uvs = op === 'set' ? new Set(picked) : new Set(sel.uvs);
  if (op === 'add') for (const uv of picked) uvs.add(uv);
  if (op === 'sub') for (const uv of picked) uvs.delete(uv);
  return makeSelection(layout, mode, { uvs });
}

/* ------------------------------------------------------------------ */
/* Transforms                                                         */

export function boundsOf(uvs: readonly Vec2[], indices: readonly number[]): UvRect | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const i of indices) {
    const p = uvs[i];
    if (!p) continue;
    minX = Math.min(minX, p[0]);
    minY = Math.min(minY, p[1]);
    maxX = Math.max(maxX, p[0]);
    maxY = Math.max(maxY, p[1]);
  }
  return Number.isFinite(minX) ? { min: [minX, minY], max: [maxX, maxY] } : null;
}

/** Bounding-box center of some UVs (the transform pivot). */
export function pivotOf(uvs: readonly Vec2[], indices: readonly number[]): Vec2 {
  const b = boundsOf(uvs, indices);
  return b ? [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2] : [0.5, 0.5];
}

export type UvTransform =
  | { kind: 'translate'; delta: Vec2 }
  | { kind: 'rotate'; angle: number; pivot: Vec2 }
  | { kind: 'scale'; scale: Vec2; pivot: Vec2 };

/** Apply a transform to the listed UVs; returns a new array (others shared). */
export function transformUvs(uvs: readonly Vec2[], indices: readonly number[], t: UvTransform): Vec2[] {
  const out = uvs.slice();
  const cos = t.kind === 'rotate' ? Math.cos(t.angle) : 1;
  const sin = t.kind === 'rotate' ? Math.sin(t.angle) : 0;
  for (const i of new Set(indices)) {
    const p = uvs[i];
    if (!p) continue;
    if (t.kind === 'translate') out[i] = [p[0] + t.delta[0], p[1] + t.delta[1]];
    else if (t.kind === 'rotate') {
      const x = p[0] - t.pivot[0];
      const y = p[1] - t.pivot[1];
      out[i] = [t.pivot[0] + x * cos - y * sin, t.pivot[1] + x * sin + y * cos];
    } else out[i] = [t.pivot[0] + (p[0] - t.pivot[0]) * t.scale[0], t.pivot[1] + (p[1] - t.pivot[1]) * t.scale[1]];
  }
  return out;
}

/** Mirror the selection across its own center along U or V. */
export function flipUvs(uvs: readonly Vec2[], indices: readonly number[], axis: 'u' | 'v'): Vec2[] {
  return transformUvs(uvs, indices, { kind: 'scale', scale: axis === 'u' ? [-1, 1] : [1, -1], pivot: pivotOf(uvs, indices) });
}

/** Line the selection up on its min / max / center along U or V (Blender's Align). */
export function alignUvs(uvs: readonly Vec2[], indices: readonly number[], axis: 'u' | 'v', mode: 'min' | 'max' | 'center'): Vec2[] {
  const b = boundsOf(uvs, indices);
  if (!b) return uvs.slice();
  const c = axis === 'u' ? 0 : 1;
  const target = mode === 'min' ? b.min[c] : mode === 'max' ? b.max[c] : (b.min[c] + b.max[c]) / 2;
  const out = uvs.slice();
  for (const i of indices) {
    const p = uvs[i];
    if (!p) continue;
    out[i] = c === 0 ? [target, p[1]] : [p[0], target];
  }
  return out;
}

/** Pin (P) or unpin (Alt+P) UVs; returns the sorted pin list. */
export function setPins(pins: readonly number[], indices: readonly number[], pinned: boolean): number[] {
  const set = new Set(pins);
  for (const i of indices) {
    if (pinned) set.add(i);
    else set.delete(i);
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * Scale each island touched by `faces` about its own center so its texel
 * density (UV units per meter) becomes `target`. Islands without surface or
 * UV area are left alone.
 */
export function setTexelDensity(layout: UvLayout, faces: Iterable<number>, target: number): Vec2[] {
  const { faceIsland } = faceIslands(layout.faces, layout.uvs.length);
  const islands = new Set<number>();
  for (const f of faces) if (faceIsland[f] !== undefined) islands.add(faceIsland[f]!);
  let uvs = layout.uvs.slice();
  for (const island of islands) {
    const islandFaces = layout.islands[island] ?? [];
    const current = densityOf(layout, islandFaces);
    if (current <= 1e-12 || !(target > 0)) continue;
    const indices = [...uvsOfFaces(layout, islandFaces)];
    const k = target / current;
    uvs = transformUvs(uvs, indices, { kind: 'scale', scale: [k, k], pivot: pivotOf(uvs, indices) });
  }
  return uvs;
}

/* ------------------------------------------------------------------ */
/* Seams                                                              */

/** Vertex edges under the given uv edges (pairs sorted, de-duplicated). */
export function vertexEdgesOf(layout: Pick<UvLayout, 'faces' | 'meshFaces'>, uvEdgePairs: readonly (readonly [number, number])[]): [number, number][] {
  const map = uvToVertexEdges(layout);
  const out = new Map<string, [number, number]>();
  for (const [a, b] of uvEdgePairs) {
    const v = map.get(edgeKey(a, b));
    if (v) out.set(edgeKey(v[0], v[1]), v);
  }
  return [...out.values()];
}

/** Add (or with `clear` remove) seams; returns the new sorted `seams` list. */
export function editSeams(seams: readonly (readonly number[])[], edges: readonly (readonly [number, number])[], clear: boolean): [number, number][] {
  const map = new Map<string, [number, number]>();
  for (const s of seams) if (s.length >= 2) map.set(edgeKey(s[0]!, s[1]!), s[0]! < s[1]! ? [s[0]!, s[1]!] : [s[1]!, s[0]!]);
  for (const [a, b] of edges) {
    if (clear) map.delete(edgeKey(a, b));
    else map.set(edgeKey(a, b), a < b ? [a, b] : [b, a]);
  }
  return [...map.values()].sort((x, y) => x[0] - y[0] || x[1] - y[1]);
}

/**
 * Seams along island borders (Blender's Seams from Islands): mesh edges shared
 * by two faces whose corners there use different UVs. Mesh borders are not seams.
 */
export function seamsFromIslands(layout: Pick<UvLayout, 'faces' | 'meshFaces'>): [number, number][] {
  const byVertexEdge = new Map<string, { v: [number, number]; uv: string[] }>();
  layout.faces.forEach((face, f) => {
    const mesh = layout.meshFaces[f];
    if (!mesh || mesh.length !== face.length) return;
    for (let k = 0; k < face.length; k++) {
      const k2 = (k + 1) % face.length;
      const va = mesh[k]!;
      const vb = mesh[k2]!;
      if (va === vb) continue;
      // UVs ordered by vertex so both faces' corners compare equal when shared.
      const uvKey = va < vb ? `${face[k]},${face[k2]}` : `${face[k2]},${face[k]}`;
      const key = edgeKey(va, vb);
      const entry = byVertexEdge.get(key);
      if (entry) entry.uv.push(uvKey);
      else byVertexEdge.set(key, { v: va < vb ? [va, vb] : [vb, va], uv: [uvKey] });
    }
  });
  const out: [number, number][] = [];
  for (const { v, uv } of byVertexEdge.values()) {
    if (uv.length >= 2 && new Set(uv).size > 1) out.push(v);
  }
  return out.sort((x, y) => x[0] - y[0] || x[1] - y[1]);
}

/* ------------------------------------------------------------------ */
/* Document edits                                                     */

/** UVs as stored: 6 decimals keep files small and diffs readable. */
export function roundUv(value: number): number {
  const r = Math.round(value * 1e6) / 1e6;
  return Object.is(r, -0) ? 0 : r;
}

export interface MeshUvPatch {
  uvs?: readonly Vec2[];
  uvFaces?: readonly (readonly number[])[];
  uvPins?: readonly number[];
  seams?: readonly (readonly [number, number])[];
}

/**
 * Write UV fields of the `mesh` part at `path` (empty pins / seams are
 * removed). Returns the document unchanged when the part is not a mesh.
 */
export function withMeshUvs(doc: ModelDoc, path: IndexPath, patch: MeshUvPatch): ModelDoc {
  if (!meshShapeAt(doc, path)) return doc;
  return updatePart(doc, path, (part) => {
    const shape: MeshShape = { ...(part.shape as MeshShape) };
    if (patch.uvs) shape.uvs = patch.uvs.map((p) => [roundUv(p[0]), roundUv(p[1])]);
    if (patch.uvFaces) shape.uvFaces = patch.uvFaces.map((f) => f.slice());
    if (patch.uvPins) {
      if (patch.uvPins.length) shape.uvPins = patch.uvPins.slice();
      else delete shape.uvPins;
    }
    if (patch.seams) {
      if (patch.seams.length) shape.seams = patch.seams.map((s) => [s[0], s[1]]);
      else delete shape.seams;
    }
    return { ...part, shape };
  });
}
